import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useFetchContext, fetchWithContext } from "../plugins/fetch";
import { useFetch, useFetchForRefetch, useFetchParallel } from "./common";
import { useEnv } from "../plugins/env";
import qs from "qs";
import { getUserDetails } from "../config";

const STALE_TIME_WORKFLOW_DEFS = 600000; // 10 mins
const STALE_TIME_SEARCH = 60000; // 1 min

export function useWorkflowSearch(searchObj) {
  const fetchContext = useFetchContext();
  const pathRoot = "/wolkenWorkflow/search?";
  // const pathRoot = "/workflow/search?"; //old

  return useQuery(
    [fetchContext.stack, pathRoot, searchObj],
    () => {
      const { rowsPerPage, page, sort, freeText, query } = searchObj;
      const path =
        pathRoot +
        qs.stringify({
          start: (page - 1) * rowsPerPage,
          size: rowsPerPage,
          sort: sort,
          freeText: freeText,
          query: query,
        });
      return fetchWithContext(path, fetchContext);
      // staletime to ensure stable view when paginating back and forth (even if underlying results change)
    },
    {
      enabled: fetchContext.ready,
      keepPreviousData: true,
      staleTime: STALE_TIME_SEARCH,
    }
  );
}

export function useWorkflow(workflowId) {
  return useFetch(["workflow", workflowId], `/workflow/${workflowId}`, {
    enabled: !!workflowId,
  });
}

export function useWorkflowsByIds(workflowIds, reactQueryOptions) {
  return useFetchParallel(
    workflowIds.map((workflowId) => ["workflow", workflowId]),
    reactQueryOptions
  );
}

export function useInvalidateWorkflows() {
  const { stack } = useEnv();
  const client = useQueryClient();

  return function (workflowIds) {
    client.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === stack &&
        query.queryKey[1] === "workflow" &&
        workflowIds.includes(query.queryKey[2]),
    });
  };
}

export function useWorkflowDef(
  workflowName,
  version,
  defaultWorkflow,
  reactQueryOptions = {}
) {
  let path;
  const key = ["workflowDef", workflowName];
  if (workflowName) {
    path = `/metadata/workflow/${workflowName}`;
    if (version) {
      path += `?version=${version}`;
      key.push(version);
    }
  }
  let defaultWorkflowCopy = {
    ...defaultWorkflow,
    ownerEmail: getUserDetails().userEmail,
  };
  return useFetch(key, path, reactQueryOptions, defaultWorkflowCopy);
}

export function useLatestWorkflowDef(
  workflowName,
  reactQueryOptions = {},
  defaultWorkflow
) {

  let path;
  const key = ["workflowDef", workflowName];
  if (workflowName) {
    path = `/metadata/workflow/${workflowName}`;
  }
  let defaultWorkflowCopy = {
    ...defaultWorkflow,
    ownerEmail: getUserDetails().userEmail,
  };
  return useFetch(key, path, reactQueryOptions, defaultWorkflowCopy);
}

export function useWorkflowDefWithEnabled(
  workflowName,
  version,
  reactQueryOptions = {},
  defaultWorkflow
) { 
  let path;
  const key = ["workflowDef", workflowName];
  if (workflowName) {
    path = `/metadata/workflow/${workflowName}`;
    if (version) {
      path += `?version=${version}`;
      key.push(version);
    }
  }
  let defaultWorkflowCopy = {
    ...defaultWorkflow,
    ownerEmail: getUserDetails().userEmail,
  };
  return useFetch(key, path, reactQueryOptions, defaultWorkflowCopy);
}

// export function useWorkflowDefs() {
//   // return useFetch(["workflowDefs"], "/metadata/workflow", { //old
//     return useFetch(["workflowDefs"], "/wolkenWorkflow/roleToWorkflowMappings", {
//     staleTime: STALE_TIME_WORKFLOW_DEFS,
//   });
// }

export function useWorkflowDefs(
  page = 0,
  limit = 15,
  name = "",
  category = ""
) {
  let offset = (page - 1) * limit + 1;
  if (offset < 0) offset = 0;

  let query = `/wolkenWorkflow/workflowDefinitions?offset=${offset}&limit=${limit}`;

  if (name) {
    query += `&name=${encodeURIComponent(name)}`;
  }

  if (category) {
    query += `&category=${encodeURIComponent(category)}`;
  }

  let response = useFetch(["workflow", page, limit, name, category], query, {
    staleTime: STALE_TIME_WORKFLOW_DEFS,
  });

  return response;
}

export function useWorkflowDefsWithoutLimit(

  name = "",
  category = ""
) {
 

  let query = `/wolkenWorkflow/workflowDefinitions`;

  if (name) {
    query += `&name=${encodeURIComponent(name)}`;
  }

  if (category) {
    query += `&category=${encodeURIComponent(category)}`;
  }

  let response = useFetch(["workflow", name, category], query, {
    staleTime: STALE_TIME_WORKFLOW_DEFS,
  });

  return response;
}

export function useLatestWorkflowDefs(
  offset = 0,
  limit = 15,
  name = "",
  category = ""
) {
  const { data: wfDefData, ...rest } = useWorkflowDefs(
    offset,
    limit,
    name,
    category
  );
  // Initialize totalCount and workflows
  let totalCount = 0;
  const workflows = useMemo(() => {
    if (wfDefData && Array.isArray(wfDefData.workflowDefinitions)) {
      totalCount = wfDefData.totalCount || 0;
      const unique = new Map();
      for (let workflowDef of wfDefData.workflowDefinitions) {
        if (!unique.has(workflowDef.name)) {
          unique.set(workflowDef.name, workflowDef);
        } else if (unique.get(workflowDef.name).version < workflowDef.version) {
          unique.set(workflowDef.name, workflowDef);
        }
      }
      return Array.from(unique.values());
    }
    return []; // Return an empty array if data or workflowDefinitions is not available
  }, [wfDefData]);

  let values = {
    data: workflows,
    totalCount: totalCount,
    ...rest,
  };

  return values;
}

export function useLatestWorkflowDefsWithoutLimit(

) {
  const { data: wfDefData, ...rest } = useWorkflowDefsWithoutLimit(
    
  );
  // Initialize totalCount and workflows
  let totalCount = 0;
  const workflows = useMemo(() => {
    if (wfDefData && Array.isArray(wfDefData.workflowDefinitions)) {
      totalCount = wfDefData.totalCount || 0;
      const unique = new Map();
      for (let workflowDef of wfDefData.workflowDefinitions) {
        if (!unique.has(workflowDef.name)) {
          unique.set(workflowDef.name, workflowDef);
        } else if (unique.get(workflowDef.name).version < workflowDef.version) {
          unique.set(workflowDef.name, workflowDef);
        }
      }
      return Array.from(unique.values());
    }
    return []; // Return an empty array if data or workflowDefinitions is not available
  }, [wfDefData]);

  let values = {
    data: workflows,
    totalCount: totalCount,
    ...rest,
  };

  return values;
}

export function useSaveWorkflow(callbacks) {
  const path = "/metadata/workflow";
  const fetchContext = useFetchContext();

  return useMutation(
    ({ body, isNew }) =>
      fetchWithContext(path, fetchContext, {
        method: isNew ? "post" : "put",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(isNew ? body : [body]),
      }),
    callbacks
  );
}

// export function useWorkflowNames(offset = 0, limit = 15) {
//   const { data } = useWorkflowDefs(offset, limit);
//   // Extract unique names
//   return useMemo(() => {
//     if (data) {
//       const nameSet = new Set(data.workflowDefinitions.map((def) => def.name));
//       return Array.from(nameSet);
//     } else {
//       return [];
//     }
//   }, [data]);
// }

export function useWorkflowNames() {
  const { data, refetch , isFetching } = useFetch(
    ["workflowDefs"],
    "/wolkenWorkflow/workflowNames?category=true",
    {
      staleTime: STALE_TIME_WORKFLOW_DEFS,
    }
  );
  const memoizedData = useMemo(() => {
    if (data) {
      return data;
    } else {
      return [];
    }
  }, [data]);

  return { data: memoizedData, refetch , isFetching };

  // Extract unique names
  // return useMemo(() => {
  //   if (data) {
  //     const nameSet = new Set(data.map((def) => def.name));
  //     return Array.from(nameSet);
  //   } else {
  //     return [];
  //   }
  // }, [data]);
}

// Version numbers do not necessarily start, or run contiguously from 1. Could be arbitrary integers e.g. 52335678.
// By convention they should be monotonic (ever increasing) wrt time.
export function useWorkflowNamesAndVersions(
  offset = 0,
  limit = 15,
  name = "",
  category = ""
) {
  const { data, ...rest } = useWorkflowDefs(offset, limit, name, category);
  var wfData = data?.workflowDefinitions;

  const newData = useMemo(() => {
    const retval = new Map();
    if (wfData) {
      for (let def of wfData) {
        let arr;
        if (!retval.has(def.name)) {
          arr = [];
          retval.set(def.name, arr);
        } else {
          arr = retval.get(def.name);
        }
        arr.push({
          version: def.version,
          createTime: def.createTime,
          updateTime: def.updateTime,
        });
      }

      // Sort arrays in place
      retval.forEach((val) => val.sort());
    }
    return retval;
  }, [wfData]);

  return { ...rest, data: newData };
}

export const useWorkflowVersions = (workflowName) => {
  var path = "/wolkenWorkflow/" + `${workflowName}` + "/versions";
  var response = null;

  response = useFetchForRefetch(["workflow"], path, {
    staleTime: STALE_TIME_WORKFLOW_DEFS,
  });

  return response;
};

export const useWorkflowVersionsWithEnabled = (workflowName,{enabled}) => {
  var path = "/wolkenWorkflow/" + `${workflowName}` + "/versions";
  var response = null;

  response = useFetchForRefetch(["workflow"], path, {
    staleTime: STALE_TIME_WORKFLOW_DEFS,
    enabled:enabled
  });

  return response;
};


export const useWorkflowVersionsDelayed = (workflowName) => {
  const [delayedWorkflowName, setDelayedWorkflowName] = useState(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelayedWorkflowName(workflowName);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [workflowName]);

  const response = useFetchForRefetch(
    ["wolkenWorkflow"],
    delayedWorkflowName
      ? `/wolkenWorkflow/${delayedWorkflowName}/versions`
      : null,
    {
      staleTime: STALE_TIME_WORKFLOW_DEFS,
    }
  );

  return response;
};

// export function useStartWorkflow(callbacks) {
//   console.log(callbacks);
//   const path = "/workflow";
//   const fetchContext = useFetchContext();

//   return useMutation(
//     ({ body }) =>

//       fetchWithContext(
//         path,
//         fetchContext,
//         {
//           method: "post",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(body),
//         },
//         false
//       ),
//     callbacks
//   );
// }

export function useStartWorkflow(callbacks) {
  const fetchContext = useFetchContext();

  return useMutation(({ body }) => {
    const path = `/workflow?version=${body.version}`;
    const requestBody = JSON.stringify({
      name: body.name,
      input: body.input,
    });

    return fetchWithContext(
      path,
      fetchContext,
      {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestBody,
      },
      false
    );
  }, callbacks);
}
